// ═══════════════════════════════════════════════════════════════
// FAQPanel.jsx — Categorized FAQ display with search and helpfulness votes
// Standalone: receives FAQ data, storage adapter, and user via props
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo, useEffect } from 'react';
import { PATHS } from './HelpCenterConfig.js';

/**
 * Props:
 *   faqs            — array of { category, icon?, questions: [{ id, question, answer }] }
 *   storage         — StorageAdapter instance
 *   user            — { id, name, email, role }
 *   primaryColor    — hex color for accent elements
 *   onContactSupport — optional callback when user clicks "Contact Support" from FAQ
 */
function FAQPanel({ faqs = [], storage, user, primaryColor = '#d97706', onContactSupport }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [votes, setVotes] = useState({});       // { faqId: 'up' | 'down' }
  const [voteCounts, setVoteCounts] = useState({}); // { faqId: { up: N, down: N } }
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Load user's existing votes on mount
  useEffect(() => {
    if (!storage || !user?.id) return;
    let cancelled = false;
    const loadVotes = async () => {
      try {
        const allVotes = {};
        for (const cat of faqs) {
          for (const q of cat.questions || []) {
            const vote = await storage.get(`${PATHS.faqVotes(q.id)}/users/${user.id}`);
            if (vote && !cancelled) allVotes[q.id] = vote.vote;
          }
        }
        if (!cancelled) setVotes(allVotes);
      } catch (e) { /* best effort */ }
    };
    loadVotes();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Load aggregate vote counts on mount
  useEffect(() => {
    if (!storage) return;
    let cancelled = false;
    const loadCounts = async () => {
      try {
        const counts = {};
        for (const cat of faqs) {
          for (const q of cat.questions || []) {
            const data = await storage.get(PATHS.faqVotes(q.id));
            if (data && !cancelled) {
              const users = data.users || {};
              const up = Object.values(users).filter(v => v.vote === 'up').length;
              const down = Object.values(users).filter(v => v.vote === 'down').length;
              counts[q.id] = { up, down };
            }
          }
        }
        if (!cancelled) setVoteCounts(counts);
      } catch (e) { /* best effort */ }
    };
    loadCounts();
    return () => { cancelled = true; };
  }, []);

  // Filter FAQs by search term
  const filteredFaqs = useMemo(() => {
    if (!searchTerm.trim()) return faqs;
    const term = searchTerm.toLowerCase();
    return faqs
      .map(cat => ({
        ...cat,
        questions: (cat.questions || []).filter(
          q => q.question.toLowerCase().includes(term) || q.answer.toLowerCase().includes(term)
        ),
      }))
      .filter(cat => cat.questions.length > 0);
  }, [faqs, searchTerm]);

  // Handle vote
  const handleVote = async (faqId, vote) => {
    if (!storage || !user?.id) return;
    const currentVote = votes[faqId];
    const newVote = currentVote === vote ? null : vote; // toggle off if same vote

    // Optimistic update
    setVotes(prev => {
      const next = { ...prev };
      if (newVote) next[faqId] = newVote;
      else delete next[faqId];
      return next;
    });

    // Update counts optimistically
    setVoteCounts(prev => {
      const current = prev[faqId] || { up: 0, down: 0 };
      const next = { ...current };
      // Remove old vote
      if (currentVote === 'up') next.up = Math.max(0, next.up - 1);
      if (currentVote === 'down') next.down = Math.max(0, next.down - 1);
      // Add new vote
      if (newVote === 'up') next.up += 1;
      if (newVote === 'down') next.down += 1;
      return { ...prev, [faqId]: next };
    });

    // Persist
    try {
      if (newVote) {
        await storage.set(`${PATHS.faqVotes(faqId)}/users/${user.id}`, {
          vote: newVote,
          votedAt: new Date().toISOString(),
        });
      } else {
        await storage.remove(`${PATHS.faqVotes(faqId)}/users/${user.id}`);
      }
    } catch (e) {
      // Revert on failure
      setVotes(prev => {
        const next = { ...prev };
        if (currentVote) next[faqId] = currentVote;
        else delete next[faqId];
        return next;
      });
    }
  };

  const toggleQuestion = (id) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  const toggleCategory = (category) => {
    setExpandedCategory(prev => prev === category ? null : category);
  };

  // Icons as simple SVG components (no external dependency)
  const SearchIcon = () => (
    <svg className="w-5 h-5 text-stone-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" strokeLinecap="round" />
    </svg>
  );
  const ChevronIcon = ({ open }) => (
    <svg className={`w-5 h-5 text-stone-500 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const ThumbUpIcon = ({ filled }) => (
    <svg className="w-4 h-4" fill={filled ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const ThumbDownIcon = ({ filled }) => (
    <svg className="w-4 h-4" fill={filled ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10zM17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  if (faqs.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-stone-500">No FAQs available yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search FAQs..."
          className="w-full pl-10 pr-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent text-stone-700 placeholder-stone-500"
          style={{ '--tw-ring-color': primaryColor, focusRingColor: primaryColor }}
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-stone-500 hover:text-stone-600"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M6 18 18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
      </div>

      {/* Search Results Count */}
      {searchTerm && (
        <p className="text-sm text-stone-500">
          {filteredFaqs.reduce((sum, cat) => sum + cat.questions.length, 0)} result(s) for "{searchTerm}"
        </p>
      )}

      {/* No Results */}
      {searchTerm && filteredFaqs.length === 0 && (
        <div className="text-center py-8">
          <p className="text-stone-500 mb-3">No matching questions found.</p>
          {onContactSupport && (
            <button
              onClick={onContactSupport}
              className="px-4 py-2 text-white rounded-lg font-medium transition-colors"
              style={{ backgroundColor: primaryColor }}
            >
              Contact Support
            </button>
          )}
        </div>
      )}

      {/* FAQ Categories */}
      {filteredFaqs.map((cat) => {
        const isCatExpanded = expandedCategory === cat.category || !!searchTerm;

        return (
          <div key={cat.category} className="border border-stone-200 rounded-xl overflow-hidden">
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(cat.category)}
              className="w-full flex items-center justify-between p-4 bg-stone-50 hover:bg-stone-100 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                {cat.icon && (
                  <span className="text-stone-500">{typeof cat.icon === 'string' ? cat.icon : React.createElement(cat.icon, { className: 'w-5 h-5' })}</span>
                )}
                <span className="font-semibold text-stone-800">{cat.category}</span>
                <span className="text-xs text-stone-500 bg-stone-200 px-2 py-0.5 rounded-full">
                  {cat.questions.length}
                </span>
              </div>
              <ChevronIcon open={isCatExpanded} />
            </button>

            {/* Questions */}
            {isCatExpanded && (
              <div className="divide-y divide-stone-100">
                {cat.questions.map((q) => {
                  const isOpen = expandedId === q.id;
                  const userVote = votes[q.id];
                  const counts = voteCounts[q.id] || { up: 0, down: 0 };

                  return (
                    <div key={q.id}>
                      {/* Question */}
                      <button
                        onClick={() => toggleQuestion(q.id)}
                        className="w-full flex items-center justify-between p-4 hover:bg-stone-50 transition-colors text-left"
                      >
                        <span className="text-stone-700 font-medium pr-4">{q.question}</span>
                        <ChevronIcon open={isOpen} />
                      </button>

                      {/* Answer */}
                      {isOpen && (
                        <div className="px-4 pb-4">
                          <div className="p-4 bg-stone-50 rounded-lg text-stone-600 text-sm leading-relaxed whitespace-pre-wrap">
                            {q.answer}
                          </div>

                          {/* Helpfulness Vote */}
                          <div className="flex items-center justify-between mt-3 px-1">
                            <span className="text-xs text-stone-500">Was this helpful?</span>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => handleVote(q.id, 'up')}
                                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors ${
                                  userVote === 'up'
                                    ? 'bg-green-100 text-green-700'
                                    : 'text-stone-500 hover:text-green-600 hover:bg-green-50'
                                }`}
                              >
                                <ThumbUpIcon filled={userVote === 'up'} />
                                {counts.up > 0 && <span>{counts.up}</span>}
                              </button>
                              <button
                                onClick={() => handleVote(q.id, 'down')}
                                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors ${
                                  userVote === 'down'
                                    ? 'bg-red-100 text-red-700'
                                    : 'text-stone-500 hover:text-red-600 hover:bg-red-50'
                                }`}
                              >
                                <ThumbDownIcon filled={userVote === 'down'} />
                                {counts.down > 0 && <span>{counts.down}</span>}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Bottom CTA */}
      {!searchTerm && onContactSupport && (
        <div className="text-center pt-4 pb-2">
          <p className="text-sm text-stone-500 mb-2">Can't find what you're looking for?</p>
          <button
            onClick={onContactSupport}
            className="px-5 py-2.5 text-white rounded-lg font-medium transition-colors hover:opacity-90"
            style={{ backgroundColor: primaryColor }}
          >
            Contact Support
          </button>
        </div>
      )}
    </div>
  );
}

export default FAQPanel;
